<template>
    <section class="flex-auto p-4 overflow-y-auto">
        <base-card class="h-full w-full flex flex-col overflow-y-auto">
            <div class="bank-statement-verification">
                <div class="upload-section">
                <h2>Bank Statement Verification</h2>
                <p>Upload your bank statement (CSV or PDF) to automatically verify pending Pay-In orders</p>
                
                <div class="upload-area" @dragover.prevent @drop.prevent="handleDrop">
                    <input
                    type="file"
                    ref="fileInput"
                    @change="handleFileSelect"
                    accept=".csv,.pdf"
                    style="display: none"
                    />
                    <button @click="$refs.fileInput.click()" class="upload-btn" :disabled="processing">
                    <span v-if="!selectedFile">Choose File</span>
                    <span v-else>{{ selectedFile.name }}</span>
                    </button>
                    <p class="upload-hint">or drag and drop file here</p>
                    <p class="file-types">Supported formats: CSV, PDF</p>
                </div>

                <button
                    @click="uploadAndVerify"
                    class="verify-btn"
                    :disabled="!selectedFile || !validatorUsername || processing"
                >
                    <span v-if="!processing">Verify Payments</span>
                    <span v-else>Processing...</span>
                </button>
                </div>

                <div v-if="processing" class="processing-indicator">
                <div class="spinner"></div>
                <p>Processing bank statement and matching UTRs...</p>
                </div>

                <div v-if="report" class="report-section">
                <h3>Verification Report</h3>
                
                <div class="summary-cards">
                    <div class="card success">
                    <h4>Verified Orders</h4>
                    <p class="count">{{ report.verified.length }}</p>
                    </div>
                    <div class="card cross" v-if="(report.crossValidator || []).length > 0">
                    <h4>Cross-bank Verified</h4>
                    <p class="count">{{ report.crossValidator.length }}</p>
                    <p class="card-hint">Reassigned from another bank — money landed here</p>
                    </div>
                    <div class="card mismatch">
                    <h4>Amount Mismatch</h4>
                    <p class="count">{{ amountMismatchCount }}</p>
                    <p class="card-hint">UTR matched, amount differs — not approved</p>
                    </div>
                    <div class="card pending">
                    <h4>Other Unmatched</h4>
                    <p class="count">{{ otherUnmatchedCount }}</p>
                    <p class="card-hint">UTR not in statement / failed row</p>
                    </div>
                    <div class="card skipped">
                    <h4>Skipped (No UTR)</h4>
                    <p class="count">{{ report.skipped.length }}</p>
                    </div>
                    <!-- Manual-review bucket for matches against orders that
                         expired more than the auto-reapproval window ago. The
                         processor intentionally does not auto-act on these. -->
                    <div class="card review" v-if="(report.expiredOutOfWindow || []).length > 0">
                    <h4>Expired (Needs Review)</h4>
                    <p class="count">{{ report.expiredOutOfWindow.length }}</p>
                    <p class="card-hint">
                      Matched a UTR on an order that expired &gt; {{ report.summary && report.summary.expiredReapprovalWindowDays ? report.summary.expiredReapprovalWindowDays : 7 }} days ago — manual decision required.
                    </p>
                    </div>
                </div>

                <div class="export-buttons">
                    <button @click="exportToCSV('verified')" class="export-btn">
                    Export Verified to CSV
                    </button>
                    <button @click="exportToCSV('unmatched')" class="export-btn">
                    Export Unmatched to CSV
                    </button>
                    <button @click="exportToCSV('skipped')" class="export-btn">
                    Export Skipped to CSV
                    </button>
                    <button @click="exportToCSV('all')" class="export-btn">
                    Export Full Report to CSV
                    </button>
                </div>

                <div class="tables">
                    <div class="table-container" v-if="(report.crossValidator || []).length > 0">
                    <h4>Cross-bank Verified ({{ report.crossValidator.length }})</h4>
                    <table>
                        <thead>
                        <tr>
                            <th>Merchant Order ID</th>
                            <th>Amount</th>
                            <th>UTR</th>
                            <th>Originally Assigned</th>
                            <th>Reassigned To</th>
                            <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(order, i) in report.crossValidator" :key="(order.merchantOrderId || '') + i">
                            <td>{{ order.merchantOrderId }}</td>
                            <td>₹{{ order.amount }}</td>
                            <td>{{ order.utr }}</td>
                            <td>{{ order.previousValidator }}</td>
                            <td>{{ order.newValidator }}</td>
                            <td><span class="badge cross">Reassigned & Approved</span></td>
                        </tr>
                        </tbody>
                    </table>
                    </div>

                    <!-- Manual-review table for matches against expired-but-out-of-window
                         orders. Auto-reapproval is intentionally skipped; the operator
                         can manually approve via the regular order page if they decide
                         to honour the late payment. -->
                    <div class="table-container" v-if="(report.expiredOutOfWindow || []).length > 0">
                    <h4>Expired — Needs Manual Review ({{ report.expiredOutOfWindow.length }})</h4>
                    <p class="table-hint">
                      Each row matched a UTR in the uploaded statement against an order that expired more
                      than {{ report.summary && report.summary.expiredReapprovalWindowDays ? report.summary.expiredReapprovalWindowDays : 7 }} days ago. Auto-reapproval is intentionally skipped. Confirm individually
                      if the late settlement is legitimate — the merchant callback fires on approval.
                    </p>
                    <table>
                        <thead>
                        <tr>
                            <th>Merchant Order ID</th>
                            <th>Order Amount</th>
                            <th>Statement Amount</th>
                            <th>UTR</th>
                            <th>Current Bank</th>
                            <th>Will Reassign To</th>
                            <th>Expired At</th>
                            <th>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(order, i) in report.expiredOutOfWindow" :key="(order.merchantOrderId || order.refID || '') + i">
                            <td>{{ order.merchantOrderId }}</td>
                            <td>₹{{ order.amount }}</td>
                            <td>{{ order.statementAmount != null ? '₹' + order.statementAmount : '-' }}</td>
                            <td>{{ order.utr }}</td>
                            <td>{{ order.currentValidator || '-' }}</td>
                            <td>{{ order.wouldReassignTo ? order.wouldReassignTo : '(same bank)' }}</td>
                            <td>{{ order.expiredAt || '-' }}</td>
                            <td class="action-cell">
                                <span v-if="order._state === 'approved'" class="badge success">Approved</span>
                                <span v-else-if="order._state === 'dismissed'" class="badge skipped">Dismissed</span>
                                <template v-else>
                                    <button
                                        @click="confirmApproveLate(order, i)"
                                        :disabled="!!order._busy"
                                        class="action-btn approve">
                                        {{ order._busy ? 'Approving…' : 'Approve' }}
                                    </button>
                                    <button
                                        @click="dismissLate(i)"
                                        :disabled="!!order._busy"
                                        class="action-btn dismiss">
                                        Dismiss
                                    </button>
                                </template>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                    </div>

                    <div class="table-container" v-if="report.verified.length > 0">
                    <h4>Verified Orders ({{ report.verified.length }})</h4>
                    <table>
                        <thead>
                        <tr>
                            <th>Receipt ID</th>
                            <th>Customer Name</th>
                            <th>Amount</th>
                            <th>Customer UTR</th>
                            <th>Matched Text</th>
                            <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="order in report.verified" :key="order.refID">
                            <td>{{ order.receiptId }}</td>
                            <td>{{ order.customerName }}</td>
                            <td>₹{{ order.amount }}</td>
                            <td>{{ order.customerUtr }}</td>
                            <td class="matched-text">{{ order.matchedText }}</td>
                            <td><span class="badge success">Approved</span></td>
                        </tr>
                        </tbody>
                    </table>
                    </div>

                    <div class="table-container" v-if="report.unmatched.length > 0">
                    <h4>Unmatched Orders ({{ report.unmatched.length }})</h4>
                    <table>
                        <thead>
                        <tr>
                            <th>Receipt ID</th>
                            <th>Customer Name</th>
                            <th>Order Amount</th>
                            <th>Statement Amount</th>
                            <th>Customer UTR</th>
                            <th>Reason</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(order, i) in report.unmatched" :key="(order.merchantOrderId || '') + i">
                            <td>{{ order.receiptId || order.merchantOrderId }}</td>
                            <td>{{ order.customerName || '-' }}</td>
                            <td>₹{{ order.amount }}</td>
                            <td>{{ order.statementAmount != null ? '₹' + order.statementAmount : '-' }}</td>
                            <td>{{ order.utr || order.customerUtr }}</td>
                            <td><span :class="['badge', reasonBadgeClass(order.reasonCode)]">{{ reasonLabel(order.reasonCode) }}</span></td>
                        </tr>
                        </tbody>
                    </table>
                    </div>

                    <div class="table-container" v-if="report.skipped.length > 0">
                    <h4>Skipped Orders ({{ report.skipped.length }})</h4>
                    <table>
                        <thead>
                        <tr>
                            <th>Receipt ID</th>
                            <th>Customer Name</th>
                            <th>Amount</th>
                            <th>Reason</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="order in report.skipped" :key="order.refID">
                            <td>{{ order.receiptId }}</td>
                            <td>{{ order.customerName }}</td>
                            <td>₹{{ order.amount }}</td>
                            <td><span class="badge skipped">No UTR</span></td>
                        </tr>
                        </tbody>
                    </table>
                    </div>
                </div>
                </div>

                <div v-if="error" class="error-message">
                {{ error }}
                </div>
            </div>
        </base-card>
    </section>
</template>

<script>
import http from '@/http-common.js';

export default {
  name: 'BankStatementVerification',
  data() {
    return {
      selectedFile: null,
      processing: false,
      report: null,
      error: null
    };
  },
  computed: {
    validatorUsername() {
      return this.$store.getters.username;
    },
    amountMismatchCount() {
      return this.report?.summary?.byReason?.amountMismatch ?? 0;
    },
    otherUnmatchedCount() {
      const total = this.report?.unmatched?.length || 0;
      return total - this.amountMismatchCount;
    }
  },
  methods: {
    // Operator confirms a single out-of-window expired match. Mirrors the
    // auto-pass logic on the backend (approve + side effects + sockets) and
    // optionally reassigns to the bank that uploaded the statement if the
    // match was cross-validator. SweetAlert wording spells out the merchant
    // callback consequence so the operator can't say they weren't warned.
    async confirmApproveLate(order, index) {
      const willReassign = !!order.wouldReassignTo && order.wouldReassignTo !== order.currentValidator;
      const detail = willReassign
        ? `This will REASSIGN the order from <b>${order.currentValidator || '(unknown)'}</b> to <b>${order.wouldReassignTo}</b> and approve it.`
        : `This will approve the order on its current bank <b>${order.currentValidator || '(unknown)'}</b>.`;
      const html = `
        <div style="text-align:left;font-size:13px;">
          <p><b>Order ID:</b> ${order.merchantOrderId || order.refID}</p>
          <p><b>Amount:</b> ₹${order.amount}</p>
          <p><b>UTR:</b> ${order.utr}</p>
          <p><b>Expired at:</b> ${order.expiredAt || '-'}</p>
          <p>${detail}</p>
          <p style="color:#B45309;"><b>The merchant will receive an approved callback for an order they were previously told had expired.</b> Confirm you've checked with the merchant if their integration requires it.</p>
        </div>
      `;
      const result = await this.$swal.fire({
        title: 'Approve late expired order?',
        html,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#16A34A',
        cancelButtonColor: '#6B7280',
        confirmButtonText: 'Approve',
        cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;

      // Per-row in-flight flag so the button shows progress + can't be
      // double-clicked. Uses splice to keep Vue's reactivity happy.
      const list = this.report.expiredOutOfWindow;
      list.splice(index, 1, { ...order, _busy: true });

      try {
        const refID = order.refID;
        if (!refID) throw new Error('Order missing refID — cannot approve without it.');
        const payload = {
          utr: order.utr,
          reassignTo: willReassign ? order.wouldReassignTo : null,
          statementSource: order.source || null,
        };
        const response = await http.post(`/orders/${refID}/approve-late-expired`, payload);
        if (response.data && response.data.success) {
          list.splice(index, 1, { ...order, _busy: false, _state: 'approved' });
          this.$swal.fire({
            icon: 'success',
            title: 'Approved',
            text: `Order ${order.merchantOrderId} approved.`,
            timer: 1500,
            showConfirmButton: false,
          });
        } else {
          throw new Error((response.data && response.data.message) || 'Approval failed.');
        }
      } catch (err) {
        list.splice(index, 1, { ...order, _busy: false });
        const msg = (err.response && err.response.data && err.response.data.message) || err.message || 'Approval failed.';
        this.$swal.fire({ icon: 'error', title: 'Approval failed', text: msg });
      }
    },
    // Local-only dismiss: marks the row as handled in this report view so
    // the operator can work through the list. No DB write.
    dismissLate(index) {
      const list = this.report.expiredOutOfWindow;
      const order = list[index];
      list.splice(index, 1, { ...order, _state: 'dismissed' });
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },
    handleDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },
    validateAndSetFile(file) {
      const validTypes = ['text/csv', 'application/pdf', 'application/vnd.ms-excel'];
      const validExtensions = ['.csv', '.pdf'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        this.error = 'Please upload a CSV or PDF file';
        return;
      }
      
      this.selectedFile = file;
      this.error = null;
      this.report = null;
    },
    async uploadAndVerify() {
      if (!this.selectedFile || !this.validatorUsername) return;

      this.processing = true;
      this.error = null;
      this.report = null;

      try {
        const formData = new FormData();
        formData.append('bankStatement', this.selectedFile);
        formData.append('validatorUsername', this.validatorUsername);

        const response = await http.post('/orders/verify-bank-statement', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.status === 200) {
          this.report = response.data.report;
        }
      } catch (error) {
        console.error('Error verifying bank statement:', error);
        this.error = error.response?.data?.message || 'Failed to process bank statement';
      } finally {
        this.processing = false;
      }
    },
    exportToCSV(type) {
      let data, csv;
      
      if (type === 'verified') {
        data = this.report.verified;
        csv = 'Receipt ID,Customer Name,Amount,Customer UTR,Matched Text,Status\n';
        data.forEach(order => {
          csv += `${order.receiptId},${order.customerName},${order.amount},${order.customerUtr},"${order.matchedText}",Approved\n`;
        });
      } else if (type === 'unmatched') {
        data = this.report.unmatched;
        csv = 'Receipt ID,Customer Name,Order Amount,Statement Amount,Customer UTR,Reason\n';
        data.forEach(order => {
          const stmtAmt = order.statementAmount != null ? order.statementAmount : '';
          csv += `${order.receiptId || order.merchantOrderId || ''},${order.customerName || ''},${order.amount},${stmtAmt},${order.utr || order.customerUtr || ''},"${this.reasonLabel(order.reasonCode)}"\n`;
        });
      } else if (type === 'skipped') {
        data = this.report.skipped;
        csv = 'Receipt ID,Customer Name,Amount,Reason\n';
        data.forEach(order => {
          csv += `${order.receiptId},${order.customerName},${order.amount},No UTR\n`;
        });
      } else if (type === 'all') {
        csv = 'Receipt ID,Customer Name,Order Amount,Statement Amount,Customer UTR,Status,Reason\n';
        this.report.verified.forEach(order => {
          const stmtAmt = order.statementAmount != null ? order.statementAmount : '';
          csv += `${order.receiptId || order.merchantOrderId || ''},${order.customerName || ''},${order.amount},${stmtAmt},${order.utr || order.customerUtr || ''},Approved,\n`;
        });
        (this.report.crossValidator || []).forEach(order => {
          const stmtAmt = order.statementAmount != null ? order.statementAmount : '';
          const note = `Reassigned from ${order.previousValidator} to ${order.newValidator}`;
          csv += `${order.merchantOrderId || ''},,${order.amount},${stmtAmt},${order.utr || ''},Reassigned & Approved,"${note}"\n`;
        });
        this.report.unmatched.forEach(order => {
          const stmtAmt = order.statementAmount != null ? order.statementAmount : '';
          csv += `${order.receiptId || order.merchantOrderId || ''},${order.customerName || ''},${order.amount},${stmtAmt},${order.utr || order.customerUtr || ''},Pending,"${this.reasonLabel(order.reasonCode)}"\n`;
        });
        this.report.skipped.forEach(order => {
          csv += `${order.receiptId || order.merchantOrderId || ''},${order.customerName || ''},${order.amount},,,Skipped,No UTR\n`;
        });
      } else {
        return;
      }

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_orders_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    exportToPDF() {
      window.print();
    },
    reasonLabel(code) {
      const map = {
        AMOUNT_MISMATCH: 'Amount mismatch',
        UTR_NOT_FOUND: 'UTR not in statement',
        STATEMENT_FAILED: 'Statement marked failed',
        VALIDATOR_UPI_MISMATCH: 'Validator UPI mismatch',
        ALREADY_PROCESSED: 'Already processed',
        ERROR: 'Error',
      };
      return map[code] || 'Pending';
    },
    reasonBadgeClass(code) {
      if (code === 'AMOUNT_MISMATCH') return 'mismatch';
      if (code === 'STATEMENT_FAILED') return 'failed';
      return 'pending';
    }
  }
};
</script>

<style scoped>
.bank-statement-verification {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #2196F3;
}

.form-control:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.upload-section {
  background: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 30px;
}

.upload-section h2 {
  margin: 0 0 10px 0;
  color: #333;
}

.upload-section > p {
  color: #666;
  margin-bottom: 30px;
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  margin-bottom: 20px;
  transition: border-color 0.3s;
}

.upload-area:hover {
  border-color: #4CAF50;
}

.upload-btn {
  background: #4CAF50;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.3s;
}

.upload-btn:hover:not(:disabled) {
  background: #45a049;
}

.upload-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.upload-hint {
  margin: 15px 0 5px 0;
  color: #999;
  font-size: 14px;
}

.file-types {
  color: #999;
  font-size: 12px;
  margin: 0;
}

.verify-btn {
  background: #2196F3;
  color: white;
  border: none;
  padding: 14px 40px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background 0.3s;
  width: 100%;
}

.verify-btn:hover:not(:disabled) {
  background: #0b7dda;
}

.verify-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.processing-indicator {
  text-align: center;
  padding: 40px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2196F3;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.report-section {
  background: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.report-section h3 {
  margin: 0 0 30px 0;
  color: #333;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.card {
  padding: 25px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card.success {
  background: #E8F5E9;
  border-left: 4px solid #4CAF50;
}

.card.pending {
  background: #FFF3E0;
  border-left: 4px solid #FF9800;
}

.card.skipped {
  background: #F5F5F5;
  border-left: 4px solid #9E9E9E;
}

.card.mismatch {
  background: #FFEBEE;
  border-left: 4px solid #E53935;
}

.card.cross {
  background: #E3F2FD;
  border-left: 4px solid #1976D2;
}

/* Expired-out-of-window bucket — amber to signal "needs human attention". */
.card.review {
  background: #FFF8E1;
  border-left: 4px solid #F59E0B;
}

.card-hint {
  margin: 6px 0 0 0;
  font-size: 11px;
  color: #888;
}

.card h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
}

.card .count {
  font-size: 36px;
  font-weight: bold;
  margin: 0;
  color: #333;
}

.export-buttons {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.export-btn {
  background: #fff;
  border: 2px solid #2196F3;
  color: #2196F3;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.export-btn:hover {
  background: #2196F3;
  color: white;
}

.tables {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.table-container {
  overflow-x: auto;
}

.table-container h4 {
  margin: 0 0 15px 0;
  color: #333;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

thead {
  background: #f5f5f5;
}

th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #666;
  border-bottom: 2px solid #ddd;
}

td {
  padding: 12px;
  border-bottom: 1px solid #eee;
}

tr:hover {
  background: #f9f9f9;
}

.matched-text {
  font-family: monospace;
  font-size: 12px;
  color: #666;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
}

.badge.success {
  background: #E8F5E9;
  color: #4CAF50;
}

.badge.pending {
  background: #FFF3E0;
  color: #FF9800;
}

.badge.skipped {
  background: #F5F5F5;
  color: #9E9E9E;
}

.badge.mismatch {
  background: #FFEBEE;
  color: #C62828;
}

.badge.failed {
  background: #FCE4EC;
  color: #AD1457;
}

.badge.cross {
  background: #E3F2FD;
  color: #1976D2;
}

.badge.review {
  background: #FFF8E1;
  color: #B45309;
}

/* Per-row action buttons for the expired-review table. */
.action-cell {
  white-space: nowrap;
}
.action-btn {
  font-size: 12px;
  padding: 4px 10px;
  margin-right: 6px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease;
}
.action-btn.approve {
  background: #16A34A;
  color: #fff;
}
.action-btn.approve:hover:not(:disabled) {
  background: #15803D;
}
.action-btn.dismiss {
  background: transparent;
  color: #6B7280;
  border-color: #D1D5DB;
}
.action-btn.dismiss:hover:not(:disabled) {
  background: #F3F4F6;
}
.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.table-hint {
  margin: 0 0 10px 0;
  font-size: 12px;
  color: #6B7280;
  line-height: 1.45;
}

.error-message {
  background: #FFEBEE;
  color: #C62828;
  padding: 15px;
  border-radius: 4px;
  margin-top: 20px;
  border-left: 4px solid #C62828;
}

@media print {
  .upload-section,
  .export-buttons,
  .processing-indicator {
    display: none;
  }
  
  .report-section {
    box-shadow: none;
  }
}
</style>